/**
 * MAPA DE HABILIDADES — as habilidades do banco como um grafo.
 *
 * Três leituras do mesmo conjunto, cada uma com as SUAS conexões:
 *
 *  - **Dependência pedagógica**: colunas pela maior cadeia de pré-requisitos; as setas
 *    vão do que se aprende antes para o que depende dele.
 *  - **Assunto**: quadros por tema; as linhas ligam habilidades de temas diferentes que
 *    compartilham um assunto, com o nome do assunto na linha do nó em foco.
 *  - **Dificuldade**: uma linha por habilidade, agrupadas por competência, com a
 *    DISTRIBUIÇÃO do b̂ a priori entre as variantes — ponto na média, barra na metade
 *    central, traço da mais fácil à mais difícil. As setas são os pré-requisitos, com um
 *    teste embutido: seta tracejada é pré-requisito que saiu MAIS DIFÍCIL que o item que
 *    depende dele.
 *
 * Passar o mouse (ou o foco do teclado) sobre um nó mostra a ficha e acende as
 * conexões dele; clicar abre o item no laboratório.
 */

import { formatBR } from '../core/num.ts';
import { adjetivo, artigo, conta, nome, numeroDe, verbo } from '../core/pt.ts';
import { seedSequence } from '../core/rng.ts';
import { generate } from '../engine/generate.ts';
import { REGISTRY, type AnyTemplate } from '../engine/registry.ts';
import { COMPETENCY_LABEL } from '../schema/taxonomy.ts';
import {
  ASSUNTO_TXT,
  assuntosEmComum,
  camadas,
  dependentes,
  FICHAS,
  TEMAS,
  temaDe,
  type FichaHabilidade,
} from './habilidades.ts';
import { habilidadeDe, NIVEL_TXT } from './itemMeta.ts';
import type { ContextoApp, Vista } from './tipos.ts';
import { novoId, salvarConfig } from './config.ts';
import { carregarCalibracao } from './calibracaoSalva.ts';
import {
  carregarHistorico,
  contagemDe,
  proximasEtapas,
  situacaoDe,
  type Tentativa,
} from './historico.ts';

export type ModoMapa = 'dependencia' | 'assunto' | 'dificuldade';
type Cor = 'competencia' | 'nivel' | 'trilha';

const NS = 'http://www.w3.org/2000/svg';
const W = 1000;
const N_VARIANTES = 16;

const MODOS: readonly { readonly id: ModoMapa; readonly rotulo: string }[] = [
  { id: 'dependencia', rotulo: 'Dependência pedagógica' },
  { id: 'assunto', rotulo: 'Assunto' },
  { id: 'dificuldade', rotulo: 'Dificuldade' },
];

const CORES: readonly { readonly id: Cor; readonly rotulo: string }[] = [
  { id: 'competencia', rotulo: 'Competência' },
  { id: 'nivel', rotulo: 'Nível' },
  // A TRILHA: o que o aluno deste navegador já domina, e o que vem a seguir.
  { id: 'trilha', rotulo: 'Sua trilha' },
];

/** Nome curto da competência, para os grupos do modo dificuldade. */
const COMP_CURTO: Readonly<Record<number, string>> = {
  1: 'Números',
  2: 'Álgebra e funções',
  3: 'Geometria',
  4: 'Grandezas e medidas',
  5: 'Contagem e probabilidade',
  6: 'Estatística',
};

interface Ponto {
  readonly x: number;
  readonly y: number;
}

/** A distribuição do b̂ de uma habilidade, já em coordenadas do desenho. */
interface Glifo {
  readonly lo: number;
  readonly q1: number;
  readonly media: number;
  readonly q3: number;
  readonly hi: number;
  readonly cy: number;
  readonly valor: string;
}

interface No {
  readonly ficha: FichaHabilidade;
  readonly t: AnyTemplate;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly glifo?: Glifo;
}

interface Aresta {
  readonly de: string;
  readonly para: string;
  readonly tipo: 'prereq' | 'assunto';
  readonly rotulo?: string;
  readonly invertida?: boolean;
  /**
   * Passagens pelas colunas intermediárias, quando a seta pula colunas: um trecho
   * horizontal no VÃO entre dois nós. Sem isso, a seta passava por baixo de um nó do
   * meio e parecia sair dele.
   */
  readonly via?: readonly { readonly x0: number; readonly x1: number; readonly y: number }[];
}

interface Desenho {
  readonly altura: number;
  readonly nos: readonly No[];
  readonly arestas: readonly Aresta[];
  /** Fundo: cabeçalhos, quadros de tema, eixo — desenhado antes das arestas. */
  readonly fundo: readonly SVGElement[];
  readonly legenda: string;
}

interface Dificuldade {
  readonly media: number;
  readonly lo: number;
  readonly q1: number;
  readonly q3: number;
  readonly hi: number;
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Readonly<Record<string, string | number>>,
  texto?: string,
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  if (texto !== undefined) e.textContent = texto;
  return e;
}

const r1 = (x: number): number => Math.round(x * 10) / 10;

// ---------------------------------------------------------------------------
// Dados
// ---------------------------------------------------------------------------

const templatePorId = (): Map<string, AnyTemplate> =>
  new Map(REGISTRY.map((t) => [t.id as string, t]));

const competenciaDe = (t: AnyTemplate): number => habilidadeDe(t)?.competencia ?? 0;

function quantil(ordenados: readonly number[], p: number): number {
  const i = (ordenados.length - 1) * p;
  const a = Math.floor(i);
  const b = Math.ceil(i);
  const va = ordenados[a] as number;
  const vb = ordenados[b] as number;
  return va + (vb - va) * (i - a);
}

/**
 * A distribuição do b̂ a priori de cada template, sobre algumas variantes. Custa gerar
 * ~16 instâncias por item, então só roda na primeira visita ao modo.
 */
function calcularDificuldades(): Map<string, Dificuldade> {
  const out = new Map<string, Dificuldade>();
  for (const t of REGISTRY) {
    const bs: number[] = [];
    for (const s of seedSequence('mapa', 0, N_VARIANTES)) {
      const r = generate(t, s);
      if (r.status === 'ok') bs.push(r.instance.difficulty.provisionalB);
    }
    if (bs.length === 0) continue;
    bs.sort((a, b) => a - b);
    out.set(t.id as string, {
      media: bs.reduce((a, b) => a + b, 0) / bs.length,
      lo: bs[0] as number,
      q1: quantil(bs, 0.25),
      q3: quantil(bs, 0.75),
      hi: bs[bs.length - 1] as number,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Leiautes
// ---------------------------------------------------------------------------

function arestasDePrerequisito(dif?: Map<string, Dificuldade>): Aresta[] {
  const out: Aresta[] = [];
  for (const f of FICHAS) {
    for (const pre of f.prerequisitos) {
      const bPre = dif?.get(pre)?.media;
      const bItem = dif?.get(f.templateId)?.media;
      const invertida = bPre !== undefined && bItem !== undefined && bPre > bItem + 0.1;
      out.push({
        de: pre,
        para: f.templateId,
        tipo: 'prereq',
        ...(invertida ? { invertida } : {}),
      });
    }
  }
  return out;
}

function leiauteDependencia(templates: Map<string, AnyTemplate>): Desenho {
  const cam = camadas();
  const L = Math.max(...cam.values()) + 1;
  const colunas: FichaHabilidade[][] = Array.from({ length: L }, () => []);
  for (const f of FICHAS) (colunas[cam.get(f.templateId) ?? 0] as FichaHabilidade[]).push(f);

  const nw = 206;
  const nh = 54;
  const passo = nh + 22;
  const topo = 52;
  const maxN = Math.max(...colunas.map((c) => c.length));
  const altura = topo + maxN * passo + 10;
  const colX = (i: number): number => 20 + (L === 1 ? 0 : (i * (W - 40 - nw)) / (L - 1));

  // Ordem dentro de cada coluna: a primeira por competência; as seguintes pela média
  // da posição dos pré-requisitos (baricentro), o que desembaraça boa parte das setas.
  // Depois, uma volta para trás reordena a primeira coluna pelos dependentes.
  const pos = new Map<string, number>();
  const ordenar = (i: number, chave: (f: FichaHabilidade) => number): void => {
    const col = colunas[i] as FichaHabilidade[];
    col.sort((a, b) => chave(a) - chave(b));
    col.forEach((f, j) => pos.set(f.templateId, j + (maxN - col.length) / 2));
  };
  const comp = (f: FichaHabilidade): number =>
    competenciaDe(templates.get(f.templateId) as AnyTemplate);
  const baricentro = (ids: readonly string[], reserva: number): number => {
    const ps = ids.map((id) => pos.get(id)).filter((p): p is number => p !== undefined);
    return ps.length ? ps.reduce((a, b) => a + b, 0) / ps.length : reserva;
  };
  ordenar(0, comp);
  for (let i = 1; i < L; i++) ordenar(i, (f) => baricentro(f.prerequisitos, 99));
  ordenar(0, (f) => baricentro(dependentes(f.templateId), 50 + comp(f)));
  for (let i = 1; i < L; i++) ordenar(i, (f) => baricentro(f.prerequisitos, 99));

  const nos: No[] = [];
  colunas.forEach((col, i) => {
    for (const f of col) {
      nos.push({
        ficha: f,
        t: templates.get(f.templateId) as AnyTemplate,
        x: colX(i),
        y: topo + (pos.get(f.templateId) ?? 0) * passo,
        w: nw,
        h: nh,
      });
    }
  });

  const cabecalhos = [
    'Sem pré-requisito',
    'Uma etapa depois',
    'Duas etapas depois',
    'Três etapas depois',
    'Quatro etapas depois',
  ];
  const fundo: SVGElement[] = colunas.map((_c, i) =>
    el(
      'text',
      { class: 'mapa-cabecalho', x: r1(colX(i) + nw / 2), y: 26, 'text-anchor': 'middle' },
      cabecalhos[i] ?? `${conta(i, 'etapa')} depois`,
    ),
  );

  const noDe = new Map(nos.map((n) => [n.ficha.templateId, n]));
  const arestas = arestasDePrerequisito().map((a): Aresta => {
    const i = cam.get(a.de) ?? 0;
    const j = cam.get(a.para) ?? 0;
    const de = noDe.get(a.de);
    const para = noDe.get(a.para);
    if (j - i < 2 || !de || !para) return a;
    const via: { x0: number; x1: number; y: number }[] = [];
    for (let k = i + 1; k < j; k++) {
      const col = nos
        .filter((n) => (cam.get(n.ficha.templateId) ?? 0) === k)
        .sort((p, q) => p.y - q.y);
      const primeiro = col[0]?.y ?? topo;
      const ultimo = col.at(-1)?.y ?? topo;
      const vaos = [
        primeiro - 11,
        ...col.slice(1).map((n, m) => ((col[m] as No).y + nh + n.y) / 2),
        ultimo + nh + 11,
      ];
      // O vão mais perto de onde a reta entre as duas pontas cruzaria a coluna.
      const alvo = de.y + nh / 2 + ((para.y - de.y) * (k - i)) / (j - i);
      const y = vaos.reduce((b, v) => (Math.abs(v - alvo) < Math.abs(b - alvo) ? v : b));
      via.push({ x0: colX(k), x1: colX(k) + nw, y });
    }
    return { ...a, via };
  });

  return {
    altura: altura + 12,
    nos,
    arestas,
    fundo,
    legenda:
      'As setas vão de cada pré-requisito para a habilidade que depende dele. A coluna diz quantas etapas de ' +
      'pré-requisito vêm antes, pela cadeia mais longa.',
  };
}

function leiauteAssunto(templates: Map<string, AnyTemplate>): Desenho {
  const cols = 4;
  const cw = 232;
  const gap = 18;
  const x0 = (W - (cols * cw + (cols - 1) * gap)) / 2;
  const nh = 46;
  const passo = nh + 10;
  const cab = 36;

  const grupos = TEMAS.map((tema) => ({
    tema,
    fichas: FICHAS.filter((f) => temaDe(f) === tema.id),
  })).filter((g) => g.fichas.length > 0);
  const alturaDe = (n: number): number => cab + n * passo + 6;

  const nos: No[] = [];
  const fundo: SVGElement[] = [];
  let y = 10;
  for (let linha = 0; linha * cols < grupos.length; linha++) {
    const daLinha = grupos.slice(linha * cols, linha * cols + cols);
    const h = Math.max(...daLinha.map((g) => alturaDe(g.fichas.length)));
    daLinha.forEach((g, i) => {
      const cx = x0 + i * (cw + gap);
      fundo.push(el('rect', { class: 'mapa-tema', x: r1(cx), y, width: cw, height: h, rx: 14 }));
      fundo.push(el('text', { class: 'mapa-tema-nome', x: r1(cx + 14), y: y + 23 }, g.tema.nome));
      g.fichas.forEach((f, j) => {
        nos.push({
          ficha: f,
          t: templates.get(f.templateId) as AnyTemplate,
          x: cx + 12,
          y: y + cab + j * passo,
          w: cw - 24,
          h: nh,
        });
      });
    });
    y += h + gap;
  }

  const arestas: Aresta[] = [];
  for (let i = 0; i < FICHAS.length; i++) {
    for (let j = i + 1; j < FICHAS.length; j++) {
      const a = FICHAS[i] as FichaHabilidade;
      const b = FICHAS[j] as FichaHabilidade;
      if (temaDe(a) === temaDe(b)) continue;
      const comum = assuntosEmComum(a, b);
      if (comum.length === 0) continue;
      arestas.push({
        de: a.templateId,
        para: b.templateId,
        tipo: 'assunto',
        rotulo: comum.map((x) => ASSUNTO_TXT[x]).join(', '),
      });
    }
  }

  return {
    altura: y,
    nos,
    arestas,
    fundo,
    legenda:
      'Os quadros agrupam as habilidades pelo tema do assunto principal. As linhas ligam habilidades de temas ' +
      'diferentes que compartilham um assunto; passe o mouse sobre uma habilidade para ver o nome do assunto.',
  };
}

/**
 * Uma linha por habilidade, com a distribuição do b̂ desenhada na escala: a leitura é a
 * de um gráfico de faixas, e não a de caixas de largura fixa. Uma habilidade de
 * dificuldade estável aparece como um ponto; uma que varia muito entre as variantes,
 * como um traço longo — e isso, por si, já é informação sobre o item.
 */
function leiauteDificuldade(
  templates: Map<string, AnyTemplate>,
  dif: Map<string, Dificuldade>,
): Desenho {
  const rotW = 262;
  const rotH = 26;
  const passo = rotH + 8;
  const x0Plot = 300;
  const x1Plot = W - 64;
  const todos = [...dif.values()];
  const bMin = Math.floor((Math.min(...todos.map((d) => d.lo)) - 0.1) * 2) / 2;
  const bMax = Math.ceil((Math.max(...todos.map((d) => d.hi)) + 0.1) * 2) / 2;
  const xDe = (b: number): number => x0Plot + ((b - bMin) / (bMax - bMin || 1)) * (x1Plot - x0Plot);

  const comps = [
    ...new Set(FICHAS.map((f) => competenciaDe(templates.get(f.templateId) as AnyTemplate))),
  ].sort((a, b) => a - b);
  const nos: No[] = [];
  const faixas: SVGElement[] = [];
  const textos: SVGElement[] = [];
  let y = 14;
  let par = false;

  for (const c of comps) {
    textos.push(
      el(
        'text',
        { class: 'mapa-grupo-cab', x: 12, y: y + 12 },
        `C${c} · ${COMP_CURTO[c] ?? ''}`.toUpperCase(),
      ),
    );
    y += 22;
    const daComp = FICHAS.filter(
      (f) => competenciaDe(templates.get(f.templateId) as AnyTemplate) === c,
    ).sort((a, b) => (dif.get(a.templateId)?.media ?? 0) - (dif.get(b.templateId)?.media ?? 0));
    for (const f of daComp) {
      const d = dif.get(f.templateId);
      if (par)
        faixas.push(
          el('rect', { class: 'mapa-linha-fundo', x: 0, y: y - 4, width: W, height: passo, rx: 6 }),
        );
      par = !par;
      nos.push({
        ficha: f,
        t: templates.get(f.templateId) as AnyTemplate,
        x: 10,
        y,
        w: rotW,
        h: rotH,
        ...(d
          ? {
              glifo: {
                lo: xDe(d.lo),
                q1: xDe(d.q1),
                media: xDe(d.media),
                q3: xDe(d.q3),
                hi: xDe(d.hi),
                cy: y + rotH / 2,
                valor: formatBR(d.media, 2),
              },
            }
          : {}),
      });
      y += passo;
    }
    y += 10;
  }

  // Eixo: marcas de meio em meio logit, com linhas verticais discretas.
  const eixoY = y + 4;
  const grade: SVGElement[] = [];
  grade.push(el('line', { class: 'mapa-eixo', x1: x0Plot, y1: eixoY, x2: x1Plot, y2: eixoY }));
  for (let b = bMin; b <= bMax + 1e-9; b += 0.5) {
    const x = r1(xDe(b));
    grade.push(el('line', { class: 'mapa-grade', x1: x, y1: 8, x2: x, y2: eixoY }));
    grade.push(
      el(
        'text',
        { class: 'mapa-eixo-texto', x, y: eixoY + 16, 'text-anchor': 'middle' },
        formatBR(b, 1),
      ),
    );
  }
  grade.push(
    el(
      'text',
      {
        class: 'mapa-eixo-titulo',
        x: r1((x0Plot + x1Plot) / 2),
        y: eixoY + 36,
        'text-anchor': 'middle',
      },
      'Dificuldade estimada a priori (b̂, em logits) — mais à direita, mais difícil',
    ),
  );

  const arestas = arestasDePrerequisito(dif);
  const invertidas = arestas.filter((a) => a.invertida).length;
  return {
    altura: eixoY + 46,
    nos,
    arestas,
    fundo: [...faixas, ...grade, ...textos],
    legenda:
      `Cada linha é uma habilidade, com o b̂ a priori de ${conta(N_VARIANTES, 'variante')}: o ponto é a média, a barra cobre a ` +
      'metade central das variantes, e o traço vai da variante mais fácil à mais difícil. Passe o mouse sobre uma ' +
      'habilidade para ver as setas dos pré-requisitos dela. ' +
      (invertidas === 0
        ? 'Nenhum pré-requisito saiu mais difícil que a habilidade que depende dele.'
        : `As setas tracejadas, sempre à vista, marcam pré-requisito que saiu mais difícil que a habilidade que depende dele (${conta(invertidas, 'caso')}) — sinal para revisar a estimativa ou a dependência.`),
  };
}

// ---------------------------------------------------------------------------
// Arestas
// ---------------------------------------------------------------------------

type Curva = { d: string; meio: Ponto };

/** Curva entre dois nós, saindo pelo lado que olha para o outro. */
function curva(a: No, b: No, sentido: 'horizontal' | 'livre'): Curva {
  const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
  const bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
  let p0: Ponto;
  let p3: Ponto;
  let c1: Ponto;
  let c2: Ponto;
  const lado = sentido === 'horizontal' || Math.abs(bc.x - ac.x) > (a.w + b.w) / 2;
  if (lado) {
    const dir = bc.x >= ac.x ? 1 : -1;
    p0 = { x: dir > 0 ? a.x + a.w : a.x, y: ac.y };
    p3 = { x: dir > 0 ? b.x : b.x + b.w, y: bc.y };
    const d = Math.max(30, Math.abs(p3.x - p0.x) * 0.45);
    c1 = { x: p0.x + dir * d, y: p0.y };
    c2 = { x: p3.x - dir * d, y: p3.y };
  } else {
    const dir = bc.y >= ac.y ? 1 : -1;
    p0 = { x: ac.x, y: dir > 0 ? a.y + a.h : a.y };
    p3 = { x: bc.x, y: dir > 0 ? b.y : b.y + b.h };
    const d = Math.max(24, Math.abs(p3.y - p0.y) * 0.45);
    c1 = { x: p0.x, y: p0.y + dir * d };
    c2 = { x: p3.x, y: p3.y - dir * d };
  }
  const f = (q: Ponto): string => `${r1(q.x)} ${r1(q.y)}`;
  return {
    d: `M ${f(p0)} C ${f(c1)}, ${f(c2)}, ${f(p3)}`,
    meio: {
      x: (p0.x + 3 * c1.x + 3 * c2.x + p3.x) / 8,
      y: (p0.y + 3 * c1.y + 3 * c2.y + p3.y) / 8,
    },
  };
}

/**
 * Seta da esquerda para a direita que atravessa colunas pelos vãos: curvas de tangente
 * horizontal até cada passagem, um trecho reto pelo vão, e a curva final até o alvo.
 */
function curvaComDesvio(a: No, b: No, via: NonNullable<Aresta['via']>): Curva {
  const f = (x: number, y: number): string => `${r1(x)} ${r1(y)}`;
  const trecho = (x0: number, y0: number, x1: number, y1: number): string => {
    const d = Math.max(24, (x1 - x0) * 0.45);
    return ` C ${f(x0 + d, y0)}, ${f(x1 - d, y1)}, ${f(x1, y1)}`;
  };
  let x = a.x + a.w;
  let y = a.y + a.h / 2;
  let caminho = `M ${f(x, y)}`;
  for (const v of via) {
    caminho += trecho(x, y, v.x0, v.y) + ` L ${f(v.x1, v.y)}`;
    x = v.x1;
    y = v.y;
  }
  caminho += trecho(x, y, b.x, b.y + b.h / 2);
  const m = via[Math.floor(via.length / 2)] ?? { x0: x, x1: x, y };
  return { d: caminho, meio: { x: (m.x0 + m.x1) / 2, y: m.y } };
}

/**
 * No modo dificuldade, a seta liga as MÉDIAS das duas habilidades, arqueando para a
 * direita da mais à direita: não cruza os rótulos, e a comparação que interessa — quem
 * está mais à direita — continua à vista.
 */
function curvaEntreMedias(a: Glifo, b: Glifo): Curva {
  const p0 = { x: a.media, y: a.cy };
  const p1 = { x: b.media, y: b.cy };
  const c = { x: Math.max(p0.x, p1.x) + 36 + Math.abs(p1.y - p0.y) * 0.15, y: (p0.y + p1.y) / 2 };
  const f = (q: Ponto): string => `${r1(q.x)} ${r1(q.y)}`;
  return {
    d: `M ${f(p0)} Q ${f(c)} ${f(p1)}`,
    meio: { x: (p0.x + 2 * c.x + p1.x) / 4, y: (p0.y + 2 * c.y + p1.y) / 4 },
  };
}

// ---------------------------------------------------------------------------
// Vista
// ---------------------------------------------------------------------------

export interface VistaMapa extends Vista {
  /** Troca o modo (usado pelos testes e por links diretos). */
  modo(m: ModoMapa): void;
}

export function criarVistaMapa(ctx: ContextoApp): VistaMapa {
  const root = document.createElement('section');
  root.className = 'mapa';

  const cabeca = document.createElement('header');
  cabeca.className = 'mapa-cabeca';
  const h1 = document.createElement('h1');
  h1.textContent = 'Mapa de habilidades';
  const intro = document.createElement('p');
  intro.textContent =
    'Cada nó é uma habilidade com item no banco. Escolha o que o mapa organiza — e, com isso, quais conexões ele ' +
    'mostra. Passe o mouse sobre uma habilidade para ver a descrição; clique para abri-la em Itens.';
  cabeca.append(h1, intro);

  const controles = document.createElement('div');
  controles.className = 'mapa-controles';
  let modo: ModoMapa = 'dependencia';
  let cor: Cor = 'competencia';
  controles.append(
    grupoDeOpcoes('Organizar por', 'mapa-modo', MODOS, modo, (v) => {
      modo = v;
      desenhar();
    }),
    grupoDeOpcoes('Cor', 'mapa-cor', CORES, cor, (v) => {
      cor = v;
      desenhar();
    }),
  );

  const palco = document.createElement('div');
  palco.className = 'mapa-palco';
  // Numa tela estreita o grafo rola de lado em vez de encolher até ninguém ler os
  // rótulos. Sem um aviso, quem abre no celular vê um pedaço do mapa e conclui que o
  // mapa é aquele pedaço.
  const avisoRolar = document.createElement('p');
  avisoRolar.className = 'mapa-rolar-aviso';
  avisoRolar.textContent = 'O mapa é mais largo que a tela: arraste para o lado para ver o resto.';
  const rolagem = document.createElement('div');
  rolagem.className = 'mapa-rolagem';
  const svg = el('svg', {
    class: 'mapa-svg',
    role: 'group',
    'aria-label': 'Grafo das habilidades',
  });
  rolagem.appendChild(svg);
  const dica = document.createElement('div');
  dica.className = 'mapa-dica';
  dica.setAttribute('role', 'tooltip');
  dica.id = 'mapa-dica';
  dica.hidden = true;
  palco.append(avisoRolar, rolagem, dica);

  const legenda = document.createElement('div');
  legenda.className = 'mapa-legenda';

  root.append(cabeca, controles, palco, legenda);

  const templates = templatePorId();
  let dificuldades: Map<string, Dificuldade> | null = null;
  let focoAtual: string | null = null;
  /** O nó cuja ficha o primeiro toque mostrou: o próximo toque nele abre o item. */
  let toqueArmado: string | null = null;
  /** O clique que esse primeiro toque ainda vai disparar, e que não deve abrir nada. */
  let engolirClique = false;
  let hist: Tentativa[] = [];
  let proximas = new Set<string>();

  /** O histórico de agora: a trilha e a "Sua situação" da ficha leem daqui. */
  function lerHistorico(): void {
    hist = carregarHistorico();
    proximas = new Set(proximasEtapas(hist));
  }

  function desenhar(): void {
    lerHistorico();
    if (modo === 'dificuldade' && !dificuldades) dificuldades = calcularDificuldades();
    const d =
      modo === 'dependencia'
        ? leiauteDependencia(templates)
        : modo === 'assunto'
          ? leiauteAssunto(templates)
          : leiauteDificuldade(templates, dificuldades as Map<string, Dificuldade>);

    svg.replaceChildren();
    svg.setAttribute('viewBox', `0 0 ${W} ${r1(d.altura)}`);
    svg.dataset['modo'] = modo;

    const defs = el('defs', {});
    for (const [id, cls] of [
      ['mapa-seta', 'mapa-seta'],
      ['mapa-seta-foco', 'mapa-seta is-foco'],
      ['mapa-seta-alerta', 'mapa-seta is-alerta'],
    ] as const) {
      const m = el('marker', {
        id,
        class: cls,
        viewBox: '0 0 10 10',
        refX: 9,
        refY: 5,
        markerWidth: 7,
        markerHeight: 7,
        orient: 'auto-start-reverse',
      });
      m.appendChild(el('path', { d: 'M 0 0 L 10 5 L 0 10 z' }));
      defs.appendChild(m);
    }
    svg.appendChild(defs);

    const gFundo = el('g', { class: 'mapa-fundo' });
    for (const f of d.fundo) gFundo.appendChild(f);
    const gNos = el('g', { class: 'mapa-nos' });
    const gArestas = el('g', { class: 'mapa-arestas' });
    const gRotulos = el('g', { class: 'mapa-rotulos' });
    // No modo dificuldade as setas passam POR CIMA das linhas: elas ligam os pontos das
    // médias, que estão dentro dos nós. Nos outros modos, os nós cobrem as pontas.
    if (modo === 'dificuldade') svg.append(gFundo, gNos, gArestas, gRotulos);
    else svg.append(gFundo, gArestas, gRotulos, gNos);

    const porId = new Map(d.nos.map((n) => [n.ficha.templateId, n]));
    for (const a of d.arestas) {
      const de = porId.get(a.de);
      const para = porId.get(a.para);
      if (!de || !para) continue;
      const { d: caminho, meio } = a.via
        ? curvaComDesvio(de, para, a.via)
        : de.glifo && para.glifo
          ? curvaEntreMedias(de.glifo, para.glifo)
          : curva(de, para, modo === 'dependencia' ? 'horizontal' : 'livre');
      const marcador =
        a.tipo === 'prereq' ? (a.invertida ? 'mapa-seta-alerta' : 'mapa-seta') : null;
      const path = el('path', {
        class: `mapa-aresta ${a.tipo}${a.invertida ? ' is-invertida' : ''}`,
        d: caminho,
        'data-de': a.de,
        'data-para': a.para,
        ...(marcador ? { 'marker-end': `url(#${marcador})`, 'data-marcador': marcador } : {}),
      });
      gArestas.appendChild(path);
      if (a.rotulo) {
        gRotulos.appendChild(
          el(
            'text',
            {
              class: 'mapa-rotulo-aresta',
              x: r1(meio.x),
              y: r1(meio.y + 3.5),
              'text-anchor': 'middle',
              'data-de': a.de,
              'data-para': a.para,
            },
            a.rotulo,
          ),
        );
      }
    }

    for (const n of d.nos) gNos.appendChild(desenharNo(n));

    legenda.replaceChildren();
    const p = document.createElement('p');
    p.textContent = d.legenda;
    legenda.append(p, legendaDeCores());
    esconderDica();
  }

  function classeDeCor(t: AnyTemplate): string {
    if (cor === 'trilha') {
      const id = t.id as string;
      return `t-${situacaoDe(id, hist)}${proximas.has(id) ? ' t-proxima' : ''}`;
    }
    return cor === 'competencia' ? `c${competenciaDe(t)}` : `n${(t.level as string).slice(1)}`;
  }

  function desenharNo(n: No): SVGGElement {
    const id = n.ficha.templateId;
    const g = el('g', {
      class: `mapa-no ${classeDeCor(n.t)}`,
      transform: `translate(${r1(n.x)} ${r1(n.y)})`,
      tabindex: 0,
      role: 'button',
      'data-id': id,
      'aria-label': `${n.t.title}. ${n.ficha.descricao} Abrir em Itens.`,
      'aria-describedby': 'mapa-dica',
    });

    if (n.glifo) {
      // Uma linha do gráfico de faixas: o rótulo à esquerda, a distribuição na escala.
      const gl = n.glifo;
      const cy = gl.cy - n.y;
      g.appendChild(
        el('rect', { class: 'mapa-no-alvo', x: -10, y: -4, width: W, height: n.h + 8 }),
      );
      g.appendChild(el('rect', { class: 'mapa-no-caixa', width: r1(n.w), height: n.h, rx: 7 }));
      g.appendChild(
        el('rect', { class: 'mapa-no-faixa', x: 5, y: 5, width: 4, height: n.h - 10, rx: 2 }),
      );
      g.appendChild(
        el('text', { class: 'mapa-no-codigo', x: 16, y: n.h / 2 + 4 }, n.t.taskModel as string),
      );
      g.appendChild(
        el('text', { class: 'mapa-no-nome', x: 90, y: n.h / 2 + 4.5 }, n.ficha.nomeCurto),
      );
      g.appendChild(
        el(
          'text',
          { class: 'mapa-no-nivel', x: r1(n.w - 8), y: n.h / 2 + 4, 'text-anchor': 'end' },
          n.t.level,
        ),
      );
      const dx = (x: number): number => r1(x - n.x);
      g.appendChild(
        el('line', { class: 'mapa-b-linha', x1: dx(gl.lo), y1: cy, x2: dx(gl.hi), y2: cy }),
      );
      g.appendChild(
        el('rect', {
          class: 'mapa-b-caixa',
          x: dx(gl.q1),
          y: cy - 5,
          width: r1(Math.max(gl.q3 - gl.q1, 1.5)),
          height: 10,
          rx: 2,
        }),
      );
      g.appendChild(el('circle', { class: 'mapa-b-media', cx: dx(gl.media), cy, r: 5.5 }));
      g.appendChild(
        el(
          'text',
          { class: 'mapa-b-valor', x: dx(Math.max(gl.hi, gl.media + 6)) + 9, y: cy + 4 },
          gl.valor,
        ),
      );
    } else {
      g.appendChild(el('rect', { class: 'mapa-no-caixa', width: r1(n.w), height: n.h, rx: 10 }));
      g.appendChild(
        el('rect', { class: 'mapa-no-faixa', x: 6, y: 7, width: 4.5, height: n.h - 14, rx: 2.25 }),
      );
      g.appendChild(
        el('text', { class: 'mapa-no-codigo', x: 18, y: n.h / 2 - 5 }, n.t.taskModel as string),
      );
      g.appendChild(
        el(
          'text',
          { class: 'mapa-no-nivel', x: r1(n.w - 10), y: n.h / 2 - 5, 'text-anchor': 'end' },
          n.t.level,
        ),
      );
      g.appendChild(
        el('text', { class: 'mapa-no-nome', x: 18, y: n.h / 2 + 12 }, n.ficha.nomeCurto),
      );
      if (cor === 'trilha') {
        // Selo da trilha: ✓ para o que já está dominado, → para a próxima etapa.
        const selo = situacaoDe(id, hist) === 'dominada' ? '✓' : proximas.has(id) ? '→' : '';
        if (selo)
          g.appendChild(
            el(
              'text',
              { class: 'mapa-no-selo', x: r1(n.w - 10), y: n.h / 2 + 12, 'text-anchor': 'end' },
              selo,
            ),
          );
      }
    }

    const abrirItem = (): void => ctx.abrirNoLaboratorio(id);
    g.addEventListener('pointerenter', (e) => focar(id, e as PointerEvent));
    g.addEventListener('pointermove', (e) => posicionarDica(e as PointerEvent));
    g.addEventListener('pointerleave', (e) => {
      // No toque, levantar o dedo também é "sair" do nó — e a ficha que o toque abriu
      // sumia no mesmo instante. Ela fica até o próximo toque.
      if ((e as PointerEvent).pointerType !== 'touch') desfocar();
    });
    g.addEventListener('focus', () => focar(id));
    g.addEventListener('blur', () => desfocar());
    /*
     * NO TOQUE NÃO HÁ "PASSAR O MOUSE": o primeiro toque num nó mostra a ficha, o
     * segundo abre o item. Antes o primeiro já abria: `preventDefault` no
     * `pointerdown` não segura o clique — o navegador o dispara mesmo assim —, e a
     * ficha nunca chegava a aparecer num celular. O clique desse primeiro toque é
     * engolido aqui, à mão.
     */
    g.addEventListener('pointerdown', (e) => {
      engolirClique = false;
      if ((e as PointerEvent).pointerType !== 'touch' || toqueArmado === id) return;
      toqueArmado = id;
      engolirClique = true;
      focar(id, e as PointerEvent);
    });
    g.addEventListener('click', () => {
      if (engolirClique) {
        engolirClique = false;
        return;
      }
      toqueArmado = null;
      abrirItem();
    });
    g.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        abrirItem();
      }
    });
    return g;
  }

  function focar(id: string, e?: PointerEvent): void {
    focoAtual = id;
    const ficha = FICHAS.find((f) => f.templateId === id);
    const t = templates.get(id);
    if (!ficha || !t) return;
    svg.classList.add('tem-foco');
    const vizinhos = new Set<string>();
    for (const a of svg.querySelectorAll<SVGElement>('.mapa-aresta, .mapa-rotulo-aresta')) {
      const liga = a.dataset['de'] === id || a.dataset['para'] === id;
      a.classList.toggle('is-foco', liga);
      if (liga) {
        vizinhos.add(a.dataset['de'] as string);
        vizinhos.add(a.dataset['para'] as string);
      }
      const marcador = a.dataset['marcador'];
      if (marcador)
        a.setAttribute(
          'marker-end',
          `url(#${liga && marcador !== 'mapa-seta-alerta' ? 'mapa-seta-foco' : marcador})`,
        );
    }
    for (const n of svg.querySelectorAll<SVGGElement>('.mapa-no')) {
      const nid = n.dataset['id'] as string;
      n.classList.toggle('is-foco', nid === id);
      n.classList.toggle('is-vizinho', nid !== id && vizinhos.has(nid));
    }
    preencherDica(ficha, t);
    dica.hidden = false;
    if (e && 'clientX' in e && typeof e.clientX === 'number') posicionarDica(e);
    else posicionarJuntoDoNo(id);
  }

  function desfocar(): void {
    focoAtual = null;
    // A ficha fechou: o próximo toque num nó volta a mostrá-la antes de abrir.
    toqueArmado = null;
    svg.classList.remove('tem-foco');
    for (const a of svg.querySelectorAll<SVGElement>('.is-foco, .is-vizinho'))
      a.classList.remove('is-foco', 'is-vizinho');
    for (const a of svg.querySelectorAll<SVGElement>('.mapa-aresta')) {
      const marcador = a.dataset['marcador'];
      if (marcador) a.setAttribute('marker-end', `url(#${marcador})`);
    }
    esconderDica();
  }

  function esconderDica(): void {
    dica.hidden = true;
  }

  function preencherDica(f: FichaHabilidade, t: AnyTemplate): void {
    dica.replaceChildren();
    const titulo = document.createElement('strong');
    titulo.className = 'mapa-dica-titulo';
    titulo.textContent = t.title;
    const meta = document.createElement('span');
    meta.className = 'mapa-dica-meta';
    const comp = competenciaDe(t);
    meta.textContent = `${t.taskModel as string} · ${t.level} ${NIVEL_TXT[t.level]} · C${comp}`;
    const desc = document.createElement('p');
    desc.textContent = f.descricao;
    dica.append(titulo, meta, desc);

    const linhas: [string, string][] = [];
    const hab = habilidadeDe(t);
    if (hab) linhas.push([`Matriz ${hab.codigo as string}`, hab.descricao]);
    const compLabel = (COMPETENCY_LABEL as Readonly<Record<number, string>>)[comp];
    if (compLabel) linhas.push(['Competência', compLabel]);
    linhas.push(['Assuntos', f.assuntos.map((a) => ASSUNTO_TXT[a]).join(', ')]);
    if (cor === 'trilha' || hist.length > 0) {
      const sit = situacaoDe(f.templateId, hist);
      const c = contagemDe(f.templateId, hist);
      const texto =
        sit === 'dominada'
          ? 'Dominada: as duas últimas respostas certas, sem dica'
          : sit === 'praticando'
            ? `Praticando: ${conta(c.tentativas, 'resposta')}, ${c.acertos} ${adjetivo('certo', 'f', numeroDe(c.acertos))}`
            : 'Ainda não respondida no percurso';
      linhas.push([
        'Sua situação',
        proximas.has(f.templateId) ? `${texto} · próxima etapa da trilha` : texto,
      ]);
    }
    const nome = (id: string): string => FICHAS.find((x) => x.templateId === id)?.nomeCurto ?? id;
    if (f.prerequisitos.length > 0)
      linhas.push(['Depende de', f.prerequisitos.map(nome).join(', ')]);
    const deps = dependentes(f.templateId);
    if (deps.length > 0) linhas.push(['Abre caminho para', deps.map(nome).join(', ')]);
    const dd = dificuldades?.get(f.templateId);
    if (dd) {
      const faixa =
        dd.hi - dd.lo < 0.005
          ? 'igual em todas as variantes'
          : `metade central das variantes entre ${formatBR(dd.q1, 2)} e ${formatBR(dd.q3, 2)}; ` +
            `da mais fácil à mais difícil, de ${formatBR(dd.lo, 2)} a ${formatBR(dd.hi, 2)}`;
      linhas.push(['b̂ a priori', `média ${formatBR(dd.media, 2)} (${faixa})`]);
    }
    const cal = carregarCalibracao().get(f.templateId);
    if (cal)
      linhas.push([
        'b calibrado',
        `${formatBR(cal.b, 2)} ± ${formatBR(cal.ep, 2)}, com ${conta(cal.n, 'resposta')} de alunos`,
      ]);

    const dl = document.createElement('dl');
    for (const [k, v] of linhas) {
      const dt = document.createElement('dt');
      dt.textContent = k;
      const ddEl = document.createElement('dd');
      ddEl.textContent = v;
      dl.append(dt, ddEl);
    }
    const acao = document.createElement('span');
    acao.className = 'mapa-dica-acao';
    acao.textContent = 'Clique para abrir em Itens';
    dica.append(dl, acao);
  }

  function posicionarDica(e: PointerEvent): void {
    if (focoAtual === null) return;
    const r = palco.getBoundingClientRect();
    colocarDica(e.clientX - r.left + 16, e.clientY - r.top + 16);
  }

  function posicionarJuntoDoNo(id: string): void {
    const no = svg.querySelector<SVGGElement>(`.mapa-no[data-id="${id}"]`);
    if (!no) return;
    const r = palco.getBoundingClientRect();
    const q = no.getBoundingClientRect();
    colocarDica(q.left - r.left, q.bottom - r.top + 8);
  }

  function colocarDica(x: number, y: number): void {
    const largura = dica.offsetWidth || 320;
    const max = Math.max(8, palco.clientWidth - largura - 8);
    dica.style.left = `${Math.min(Math.max(8, x), max)}px`;
    dica.style.top = `${Math.max(8, y)}px`;
  }

  function legendaDeCores(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'mapa-cores';
    if (cor === 'trilha') {
      box.append(
        chip('t-dominada', 'Dominada'),
        chip('t-praticando', 'Praticando'),
        chip('t-nova', 'Ainda não respondida'),
        chip('t-proxima', 'Próxima etapa'),
      );
      const dominadas = FICHAS.filter((f) => situacaoDe(f.templateId, hist) === 'dominada').length;
      const resumo = document.createElement('p');
      resumo.className = 'mapa-trilha-resumo';
      resumo.textContent =
        hist.length === 0
          ? 'A trilha começa quando você responde questões no percurso. As habilidades sem pré-requisito são as primeiras etapas.'
          : `${dominadas} de ${conta(FICHAS.length, 'habilidade')} dominadas — duas respostas certas seguidas, sem dica. ` +
            `${proximas.size} ${saoAsProximas(proximas.size)}.`;
      const montar = document.createElement('button');
      montar.type = 'button';
      montar.className = 'app-btn-mini';
      montar.textContent = 'Montar percurso com as próximas etapas';
      montar.disabled = proximas.size === 0;
      montar.addEventListener('click', () => {
        const ids = FICHAS.map((f) => f.templateId).filter((id) => proximas.has(id));
        salvarConfig({
          v: 1,
          ordem: 'lista',
          entradas: ids.map((id) => ({ id: novoId(), templateId: id })),
        });
        ctx.avisar(`Percurso montado com ${asProximas(ids.length)} da trilha.`);
        ctx.irPara('montar');
      });
      box.append(resumo, montar);
      return box;
    }
    if (cor === 'competencia') {
      const comps = [...new Set(REGISTRY.map(competenciaDe))].sort((a, b) => a - b);
      for (const c of comps) box.appendChild(chip(`c${c}`, `C${c} · ${COMP_CURTO[c] ?? ''}`));
    } else {
      const niveis = [...new Set(REGISTRY.map((t) => t.level))].sort();
      for (const n of niveis) box.appendChild(chip(`n${n.slice(1)}`, `${n} · ${NIVEL_TXT[n]}`));
    }
    return box;
  }

  return {
    element: root,
    mostrar() {
      // O aluno pode ter respondido questões desde a última visita. Na trilha isso muda
      // as cores, e o mapa se redesenha; nas outras cores muda só a "Sua situação" da
      // ficha — que lia o histórico do primeiro desenho, e ficava para trás.
      if (!svg.hasChildNodes() || cor === 'trilha') desenhar();
      else lerHistorico();
    },
    esconder() {
      desfocar();
    },
    modo(m) {
      modo = m;
      const r = controles.querySelector<HTMLInputElement>(`input[name="mapa-modo"][value="${m}"]`);
      if (r) r.checked = true;
      desenhar();
    },
  };
}

function chip(classe: string, texto: string): HTMLElement {
  const s = document.createElement('span');
  s.className = `mapa-chip ${classe}`;
  s.textContent = texto;
  return s;
}

/** Grupo de opções exclusivas, desenhado como segmentos — mas com rádios de verdade por baixo. */
function grupoDeOpcoes<T extends string>(
  titulo: string,
  nome: string,
  opcoes: readonly { readonly id: T; readonly rotulo: string }[],
  inicial: T,
  aoMudar: (v: T) => void,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'mapa-grupo';
  box.setAttribute('role', 'radiogroup');
  const rot = document.createElement('span');
  rot.className = 'mapa-grupo-titulo';
  rot.id = `${nome}-titulo`;
  rot.textContent = titulo;
  box.setAttribute('aria-labelledby', rot.id);
  const seg = document.createElement('div');
  seg.className = 'mapa-segmentos';
  for (const o of opcoes) {
    const lab = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = nome;
    input.value = o.id;
    input.checked = o.id === inicial;
    input.addEventListener('change', () => {
      if (input.checked) aoMudar(o.id);
    });
    const span = document.createElement('span');
    span.textContent = o.rotulo;
    lab.append(input, span);
    seg.appendChild(lab);
  }
  box.append(rot, seg);
  return box;
}

/** "é a próxima etapa", "são as próximas etapas" — concordando com quantas são. */
function saoAsProximas(n: number): string {
  const nn = numeroDe(n);
  return `${verbo('ser.pres', nn)} ${artigo('f', nn)} ${adjetivo('proximo', 'f', nn)} ${nome('etapa', nn)}`;
}

/** "a próxima etapa", "as 3 próximas etapas" — antes, "as 1 próximas etapas". */
function asProximas(n: number): string {
  const nn = numeroDe(n);
  const quantas = nn === 'sg' ? '' : ` ${n}`;
  return `${artigo('f', nn)}${quantas} ${adjetivo('proximo', 'f', nn)} ${nome('etapa', nn)}`;
}
